/**
 * Route names + paths — mirror `watanhr-mobile/lib/core/router/route_names.dart`
 * so engineers moving between web and Flutter find identical concepts.
 *
 * Each route exports a `path` (used by react-router) and a typed
 * `build()` helper for routes that take params.
 */

export const Routes = {
  splash: { path: '/' },

  // Address picker (gate entry-point for guests + authed users without a
  // saved address). Sits outside the main tab shell so the picker can use
  // the full viewport.
  address: { path: '/address' },

  // Legal — public Terms & Conditions (UAT #11). Sits outside the tab
  // shell + outside auth: the booking wizard + login screen link here and
  // the customer may not be authenticated yet.
  terms: { path: '/terms' },

  // Public legal pages (Ask 4, 2026-06-16) — STATIC English T&C + Privacy at
  // stable URLs for App Store / Google Play submission (separate from the
  // backend-fed in-app `/terms`). No backend/auth dependency so store
  // reviewers always get a 200.
  legalTerms: { path: '/legal/terms' },
  legalPrivacy: { path: '/legal/privacy' },

  // Auth (outside the main tab shell).
  login: { path: '/login' },
  otp: { path: '/login/otp', build: (phone: string) => `/login/otp?phone=${encodeURIComponent(phone)}` },
  register: { path: '/register' },
  onboarding: { path: '/onboarding' },

  // Main tabs (rendered inside MobileShell with bottom navigation).
  home: { path: '/home' },
  bookings: { path: '/bookings' },
  wallet: { path: '/wallet' },
  account: { path: '/account' },

  // Hourly services.
  hourlyBrowse: {
    path: '/services/hourly',
    build: (categoryId?: string) =>
      categoryId
        ? `/services/hourly?categoryId=${encodeURIComponent(categoryId)}`
        : '/services/hourly',
  },
  hourlyServiceDetail: {
    path: '/services/hourly/:serviceId',
    build: (serviceId: string) => `/services/hourly/${serviceId}`,
  },
  // Dedicated category landing — hero banner + filtered service grid.
  category: {
    path: '/category/:categoryId',
    build: (categoryId: string) => `/category/${categoryId}`,
  },
  hourlyBookingWizard: {
    path: '/services/hourly/:serviceId/book',
    build: (serviceId: string) => `/services/hourly/${serviceId}/book`,
  },
  /** Multi-service entry point — opens the booking wizard with NO
   *  pre-seeded service. Customer picks one or more services in
   *  Step 2 (Services). Same component as `hourlyBookingWizard`. */
  bookingWizardOpen: { path: '/book' },

  // Resident services (Stay-in / Muqeema).
  residentBrowse: { path: '/services/resident' },
  workerProfile: {
    path: '/services/resident/workers/:workerId',
    build: (workerId: string) => `/services/resident/workers/${workerId}`,
  },
  applicationWizard: {
    path: '/services/resident/workers/:workerId/apply',
    build: (workerId: string) => `/services/resident/workers/${workerId}/apply`,
  },

  // Bookings detail flow.
  bookingDetail: {
    path: '/bookings/:bookingId',
    build: (bookingId: string) => `/bookings/${bookingId}`,
  },
  visitTracking: {
    path: '/bookings/:bookingId/visits/:visitId',
    build: (bookingId: string, visitId: string) =>
      `/bookings/${bookingId}/visits/${visitId}`,
  },
  rateVisit: {
    path: '/bookings/:bookingId/visits/:visitId/rate',
    build: (bookingId: string, visitId: string) =>
      `/bookings/${bookingId}/visits/${visitId}/rate`,
  },

  // Wallet sub-routes.
  paymentHistory: { path: '/wallet/payments' },
  loyalty: { path: '/wallet/loyalty' },
  subscriptions: { path: '/wallet/subscriptions' },
  referral: { path: '/wallet/referral' },

  // Hyperpay checkout (customer-facing post-booking).
  // `/pay/:checkoutId` hosts the COPYandPAY widget — for MOCK- prefixed
  // checkouts it renders a stub button; for real checkouts it loads
  // Hyperpay's paymentWidgets.js. `/pay/return` is the destination
  // Hyperpay (or the mock) redirects to after the customer finishes.
  payCheckout: {
    path: '/pay/:checkoutId',
    build: (checkoutId: string) => `/pay/${encodeURIComponent(checkoutId)}`,
  },
  payReturn: { path: '/pay/return' },

  // Customer location-confirm (H1 #9) — public, tokenized. The operator
  // SMSes this link; the customer pins their exact service location on a
  // map. Full-bleed, outside the shell + outside auth (the token is the
  // only credential).
  confirmLocation: {
    path: '/confirm-location/:token',
    build: (token: string) => `/confirm-location/${encodeURIComponent(token)}`,
  },

  // Account sub-routes.
  profile: { path: '/account/profile' },
  addresses: { path: '/account/addresses' },
  favorites: { path: '/account/favorites' },
  support: { path: '/account/support' },
  notifications: { path: '/account/notifications' },
} as const;
